import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map a recent vibe-check mood string to one of the 7 poetic-voice mood
// buckets (anchored | tender | rising | open | weary | joyful | seeking).
// Best-effort — falls back to null which means "any mood" on the server.
const VIBE_TO_MOOD = {
  amazing: "joyful", great: "joyful", good: "open", okay: "anchored",
  meh: "weary", tired: "weary", drained: "weary", exhausted: "weary",
  anxious: "tender", sad: "tender", grief: "tender", heavy: "tender",
  inspired: "rising", excited: "rising", determined: "rising",
  curious: "seeking", uncertain: "seeking", lost: "seeking",
  peaceful: "anchored", grounded: "anchored", calm: "anchored",
  open: "open", grateful: "open", content: "open",
};

function vibeToMood(latestVibe) {
  if (!latestVibe) return null;
  const key = String(latestVibe).toLowerCase().trim();
  return VIBE_TO_MOOD[key] || null;
}

// Local time-of-day bucket — match the backend's poetic_voices.time_bucket()
// and services/for_you_content.py time_of_day(). 5–11 morning, 12–16 midday,
// 17+ evening, 0–4 evening (night-owl).
function clientTimeBucket() {
  const h = new Date().getHours();
  if (h < 5) return "evening";
  if (h < 12) return "morning";
  if (h < 17) return "midday";
  return "evening";
}

const CollectiveIntention = () => {
  const { token } = useAuth();
  const [intention, setIntention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  const fetchTodaysIntention = useCallback(async () => {
    if (!token) return;
    try {
      // Try to read the user's most recent vibe-check so the rotation lands
      // on a voice that matches their actual day. Soft fail — if the call
      // 4xx's we still fetch the unscoped collective intention.
      let mood = null;
      try {
        const vibeRes = await axios.get(`${API}/vibe-check/today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        mood = vibeToMood(vibeRes?.data?.mood || vibeRes?.data?.vibe);
      } catch { /* no vibe today is fine */ }

      const url = mood
        ? `${API}/collective-intention/today?mood=${encodeURIComponent(mood)}`
        : `${API}/collective-intention/today`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIntention(res.data);
    } catch (error) {
      console.error("Failed to fetch collective intention:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTodaysIntention();
  }, [fetchTodaysIntention]);

  // Auto-refresh when the time-of-day bucket flips (e.g., user keeps the
  // tab open through morning → midday). Cheap interval, no network spam.
  useEffect(() => {
    let lastBucket = clientTimeBucket();
    const id = setInterval(() => {
      const nextBucket = clientTimeBucket();
      if (nextBucket !== lastBucket) {
        lastBucket = nextBucket;
        fetchTodaysIntention();
      }
    }, 5 * 60 * 1000); // check every 5 minutes
    return () => clearInterval(id);
  }, [fetchTodaysIntention]);

  const joinIntention = async () => {
    setJoining(true);
    try {
      // Re-derive mood at join time — it may have changed since we first
      // loaded (user logged a fresh vibe between page load and tap).
      let mood = null;
      try {
        const vibeRes = await axios.get(`${API}/vibe-check/today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        mood = vibeToMood(vibeRes?.data?.mood || vibeRes?.data?.vibe);
      } catch { /* fall through */ }

      const url = mood
        ? `${API}/collective-intention/join?mood=${encodeURIComponent(mood)}`
        : `${API}/collective-intention/join`;
      const res = await axios.post(
        url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(res.data.message);
        setIntention(prev => ({
          ...prev,
          is_joined: true,
          participant_count: res.data.participant_count
        }));
      }
    } catch (error) {
      toast.error("Failed to join collective intention");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/30 animate-pulse">
        <CardContent className="p-4 h-24" />
      </Card>
    );
  }

  if (!intention) return null;

  return (
    <Card 
      className="bg-gradient-to-br from-violet-600/20 via-purple-500/20 to-pink-500/20 border-violet-500/40 overflow-hidden relative"
      data-testid="collective-intention-card"
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-2 left-4 w-2 h-2 bg-violet-400/30 rounded-full animate-pulse" />
        <div className="absolute top-6 right-8 w-1.5 h-1.5 bg-pink-400/30 rounded-full animate-pulse delay-100" />
        <div className="absolute bottom-4 left-12 w-1 h-1 bg-purple-400/30 rounded-full animate-pulse delay-200" />
        <div className="absolute bottom-8 right-4 w-2 h-2 bg-violet-300/20 rounded-full animate-pulse delay-300" />
      </div>
      
      <CardContent className="p-4 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-foreground">Daily Collective Intention</h3>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                <Users className="w-3 h-3" />
                <span className="font-medium text-violet-600 dark:text-violet-400">
                  {intention.participant_count.toLocaleString()}
                </span>
                souls manifesting together
                {intention.bucket && (
                  <span
                    className="ml-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 text-[9px] uppercase tracking-wider font-semibold"
                    data-testid="intention-bucket-pill"
                  >
                    <span className="w-1 h-1 rounded-full bg-violet-500 animate-pulse" />
                    {intention.bucket}
                  </span>
                )}
              </p>
            </div>
          </div>
          <span className="text-2xl">{intention.emoji}</span>
        </div>

        {/* Intention Text — always show in full. The pool is short by design
            (1-3 lines), so line-clamping just hid the ending of longer ones. */}
        <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3 mb-3">
          <p className="text-sm text-foreground italic leading-relaxed break-words">
            "{intention.intention}"
          </p>
        </div>

        {/* Join Button */}
        {!intention.is_joined ? (
          <Button 
            onClick={joinIntention}
            disabled={joining}
            className="w-full mt-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
            size="sm"
            data-testid="join-intention-btn"
          >
            {joining ? (
              "Joining..."
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Join Today's Manifestation
              </>
            )}
          </Button>
        ) : (
          <div className="mt-3 py-2 px-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              You're manifesting with {intention.participant_count.toLocaleString()} souls today ✨
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CollectiveIntention;
